/* eslint-disable eqeqeq */
import PropTypes from 'prop-types'
import { Box, Button, Checkbox, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Textarea, VStack } from '@chakra-ui/react'
import { getCommunicationOptions, getExistingSignOptions, getFooterStyleOptions, getInstallationOptions, getPedestalDecorationOptions, getSiteSurveyOptions, getVerticalSupportOptions, upsertProjectExtraDetail } from 'api'
import { useEffect, useState } from 'react'
import { CABINET_FACES, CABINET_STYLES, EMC_FACES, EMC_PITCHES, MONUMENT_STYLES, PEDESTAL_STYLES, QUOTE_PAYMENT_TERMS_OPTIONS, CUSTOMER_PROVIDING_DEPOSIT, STACK_MODES, STRUCTURE_COMPONENT_TYPES, defaultStructureFields, parseStructureComponents, applySyncedStructureFields, isTruthyFlag } from 'lib'
import { UiSelect } from 'components/common'
import { useImmer } from 'use-immer'
import { useAppContext } from 'hooks/useAppContext'
import { StackModeField } from 'components/quotes/edge-quotes/create/StackModeField'
import { ExtraStructureComponents } from 'components/quotes/edge-quotes/create/ExtraStructureComponents'
import { EdgeOverallStructureForm } from 'components/quotes/edge-quotes/create/EdgeOverallStructureForm'

const initialForm = {
  projectId: 0,
  noEmc: true,
  emcModel: '',
  emcConfiguration: '',
  emcNominalSize: '',
  emcPitch: '',
  emcMatrix: '',
  emcCommunicationObj: '',
  noCabinet: true,
  cabinetSeries: '',
  cabinetConfiguration: '',
  cabinetNominalSize: '',
  cphra: false,
  plpr: false,
  signedArtwork: 'No',
  noMonument: true,
  monumentStyle: '',
  noPedestal: true,
  pedestalStyle: '',
  pedestalNominalSize: '',
  pedestalDecorationOptionObj: '',
  pedestalAdditionalHeight: 0,
  numberOfSupports: '',
  verticalSupportOptionObj: '',
  footerStyleOptionObj: '',
  installationOptionObj: '',
  siteSurveyOptionObj: '',
  existingSignOptionObj: '',
  fileSignedQuote: null,
  fileSignedArt: null,
  scope: '',
  customerPo: '',
  fileTaxExemption: null,
  isIndividualSchool: false,
  schoolDistrictName: '',
  alternateAccountingContact: '',
  expectedPaymentTerms: '',
  customerProvidingDeposit: CUSTOMER_PROVIDING_DEPOSIT.Unknown,
  accountingNotes: '',
  emcHeightFt: 0,
  emcLengthFt: 0,
  cabinetHeightIn: 0,
  cabinetLengthIn: 0,
  ...defaultStructureFields()
}

export const ModalProjectDetailOverview = ({ isOpen, onClose, projectId, projectExtraDetail, reload }) => {
  const { createToast } = useAppContext()
  const [form, setForm] = useImmer(initialForm)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!projectExtraDetail) {
      setForm({ ...initialForm, projectId })
    } else {
      setForm({
        projectId,
        noEmc: projectExtraDetail?.noEmc == true,
        emcModel: projectExtraDetail?.emcModel || '',
        emcConfiguration: projectExtraDetail?.emcConfiguration || '',
        emcNominalSize: projectExtraDetail?.emcNominalSize || '',
        emcPitch: projectExtraDetail?.emcPitch || '',
        emcMatrix: projectExtraDetail?.emcMatrix || '',
        emcCommunicationObj: projectExtraDetail?.emcCommunication ? { description: projectExtraDetail?.emcCommunication } : null,
        noCabinet: projectExtraDetail?.noCabinet == true,
        cabinetSeries: projectExtraDetail?.cabinetSeries || '',
        cabinetConfiguration: projectExtraDetail?.cabinetConfiguration || '',
        cabinetNominalSize: projectExtraDetail?.cabinetNominalSize || '',
        cphra: projectExtraDetail?.cphra == true,
        plpr: projectExtraDetail?.plpr == true,
        signedArtwork: projectExtraDetail?.signedArtwork || '',
        noMonument: projectExtraDetail?.noMonument == true,
        monumentStyle: projectExtraDetail?.monumentStyle || '',
        noPedestal: projectExtraDetail?.noPedestal == true,
        pedestalStyle: projectExtraDetail?.pedestalStyle || '',
        pedestalNominalSize: projectExtraDetail?.pedestalNominalSize || '',
        pedestalDecorationOptionObj: projectExtraDetail?.pedestalDecorationOption ? { description: projectExtraDetail?.pedestalDecorationOption } : null,
        pedestalAdditionalHeight: projectExtraDetail?.pedestalAdditionalHeight || 0,
        numberOfSupports: projectExtraDetail?.numberOfSupports || '',
        verticalSupportOptionObj: projectExtraDetail?.verticalSupportOption ? { description: projectExtraDetail?.verticalSupportOption } : null,
        footerStyleOptionObj: projectExtraDetail?.footerStyleOption ? { description: projectExtraDetail?.footerStyleOption } : null,
        installationOptionObj: projectExtraDetail?.installationOption ? { description: projectExtraDetail?.installationOption } : null,
        siteSurveyOptionObj: projectExtraDetail?.siteSurveyOption ? { description: projectExtraDetail?.siteSurveyOption } : null,
        existingSignOptionObj: projectExtraDetail?.existingSignOption ? { description: projectExtraDetail?.existingSignOption } : null,
        fileSignedQuote: null,
        fileSignedArt: null,
        scope: projectExtraDetail?.scope || '',
        customerPo: projectExtraDetail?.customerPo || '',
        fileTaxExemption: null,
        isIndividualSchool: projectExtraDetail?.isIndividualSchool == true,
        schoolDistrictName: projectExtraDetail?.schoolDistrictName || '',
        alternateAccountingContact: projectExtraDetail?.alternateAccountingContact || '',
        expectedPaymentTerms: projectExtraDetail?.expectedPaymentTerms || '',
        customerProvidingDeposit: projectExtraDetail?.customerProvidingDeposit || CUSTOMER_PROVIDING_DEPOSIT.Unknown,
        accountingNotes: projectExtraDetail?.accountingNotes || '',
        emcHeightFt: projectExtraDetail?.emcHeightFt || 0,
        emcLengthFt: projectExtraDetail?.emcLengthFt || 0,
        cabinetHeightIn: projectExtraDetail?.cabinetHeightIn || 0,
        cabinetLengthIn: projectExtraDetail?.cabinetLengthIn || 0,
        emcStackMode: projectExtraDetail?.emcStackMode || STACK_MODES.Stacked,
        cabinetStackMode: projectExtraDetail?.cabinetStackMode || STACK_MODES.Stacked,
        structureComponents: parseStructureComponents(projectExtraDetail?.structureComponents),
        groundClearanceFt: projectExtraDetail?.groundClearanceFt || 0,
        groundClearanceManual: isTruthyFlag(projectExtraDetail?.groundClearanceManual),
        overallHeightFt: projectExtraDetail?.overallHeightFt || 0,
        overallHeightManual: isTruthyFlag(projectExtraDetail?.overallHeightManual),
        squareFootage: projectExtraDetail?.squareFootage || 0,
        squareFootageManual: isTruthyFlag(projectExtraDetail?.squareFootageManual)
      })
    }
  }, [projectExtraDetail])

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setForm(draft => {
        draft[e.target.name] = e.target.files[0]
      })
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      const {
        emcCommunicationObj, pedestalDecorationOptionObj, verticalSupportOptionObj, footerStyleOptionObj,
        installationOptionObj, siteSurveyOptionObj, existingSignOptionObj, ...rest
      } = form
      await upsertProjectExtraDetail(projectExtraDetail?.id, {
        ...rest,
        structureComponents: JSON.stringify(rest.structureComponents || []),
        emcCommunication: emcCommunicationObj?.description || '',
        pedestalDecorationOption: pedestalDecorationOptionObj?.description || '',
        verticalSupportOption: verticalSupportOptionObj?.description || '',
        footerStyleOption: footerStyleOptionObj?.description || '',
        installationOption: installationOptionObj?.description || '',
        siteSurveyOption: siteSurveyOptionObj?.description || '',
        existingSignOption: existingSignOptionObj?.description || ''
      })
      createToast('success', 'Success', 'Project overview updated.')
      setIsSaving(false)
      onClose()
      reload()
      setForm({ ...initialForm, projectId })
    } catch (err) {
      setIsSaving(false)
      createToast('error', 'Error', err)
    }
  }

  useEffect(() => {
    setForm(draft => {
      applySyncedStructureFields(draft)
    })
  }, [
    form.noPedestal, form.pedestalNominalSize, form.pedestalStyle, form.noEmc, form.emcHeightFt, form.emcLengthFt, form.emcNominalSize, form.emcStackMode,
    form.noCabinet, form.cabinetHeightIn, form.cabinetLengthIn, form.cabinetNominalSize, form.cabinetStackMode, form.structureComponents,
    form.groundClearanceFt, form.groundClearanceManual, form.overallHeightFt, form.overallHeightManual,
    form.squareFootage, form.squareFootageManual
  ])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSave}>
          <ModalHeader>Edit Overview</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="600px" overflow="auto">
            <VStack alignItems="none" gap={4}>
              <EdgeOverallStructureForm form={form} setForm={setForm} />
              {form.noEmc == false && (
                <Box>
                  <Heading size="md" mb={2}>EMC</Heading>
                  <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Model</FormLabel>
                        <Select name="emcModel" value={form.emcModel} onChange={handleChange} placeholder="Select">
                          <option value="Impact G3">Impact G3</option>
                          <option value="Impact G3 Indoor">Impact G3 Indoor</option>
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Configuration</FormLabel>
                        <Select name="emcConfiguration" value={form.emcConfiguration} onChange={handleChange} placeholder="Select">
                          {Object.keys(EMC_FACES).map(v => (
                            <option key={EMC_FACES[v]} value={EMC_FACES[v]}>{EMC_FACES[v]}</option>
                          ))}
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Nominal Size</FormLabel>
                        <Input name="emcNominalSize" value={form.emcNominalSize} onChange={handleChange} />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Pitch</FormLabel>
                        <Select name="emcPitch" value={form.emcPitch} onChange={handleChange} placeholder="Select">
                          <option value={EMC_PITCHES.P4}>{EMC_PITCHES.P4}</option>
                          <option value={EMC_PITCHES.P6}>{EMC_PITCHES.P6}</option>
                          <option value={EMC_PITCHES.P9}>{EMC_PITCHES.P9}</option>
                          <option value={EMC_PITCHES.P15}>{EMC_PITCHES.P15}</option>
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Matrix</FormLabel>
                        <Input name="emcMatrix" value={form.emcMatrix} onChange={handleChange} />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Communication</FormLabel>
                        <UiSelect
                          value={form.emcCommunicationObj}
                          onChange={v => { setForm(draft => { draft.emcCommunicationObj = v }) }}
                          loadData={(page, search) => {
                            return getCommunicationOptions(page, 20, search, 'description', 'asc')
                          }}
                          apiRespProp="rows"
                          getOptionValue={v => v.id}
                          getOptionLabel={v => v.description}
                          placeholder="Search"
                          noOptionsMessage={() => 'Not Found'}
                          required={true}
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>
                  <Box mt={3}>
                    <StackModeField
                      value={form.emcStackMode || STACK_MODES.Stacked}
                      onChange={(v) => setForm(draft => { draft.emcStackMode = v })}
                    />
                    <ExtraStructureComponents form={form} setForm={setForm} type={STRUCTURE_COMPONENT_TYPES.Emc} />
                  </Box>
                </Box>
              )}
              {form.noCabinet == false && (
                <Box>
                  <Heading size="md" mb={2}>Cabinet</Heading>
                  <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Series</FormLabel>
                        <Select name="cabinetSeries" value={form.cabinetSeries} onChange={handleChange} placeholder="Select">
                          {Object.keys(CABINET_STYLES).map(v => (
                            <option key={CABINET_STYLES[v].value} value={CABINET_STYLES[v].value}>{CABINET_STYLES[v].label}</option>
                          ))}
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Configuration</FormLabel>
                        <Select name="cabinetConfiguration" value={form.cabinetConfiguration} onChange={handleChange} placeholder="Select">
                          {Object.keys(CABINET_FACES).map(v => (
                            <option key={CABINET_FACES[v]} value={CABINET_FACES[v]}>{CABINET_FACES[v]}</option>
                          ))}
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Nominal Size</FormLabel>
                        <Input name="cabinetNominalSize" value={form.cabinetNominalSize} onChange={handleChange} />
                      </FormControl>
                    </GridItem>
                    <GridItem></GridItem>
                    <GridItem>
                      <FormControl>
                        <Checkbox
                          isChecked={form.cphra}
                          onChange={(e) => setForm(draft => {
                            draft.cphra = e.target.checked
                          })}
                        >
                          Custom Vector Art
                        </Checkbox>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <Checkbox
                          isChecked={form.plpr}
                          onChange={(e) => setForm(draft => {
                            draft.plpr = e.target.checked
                          })}
                        >
                          Professional Logo Pkg
                        </Checkbox>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={2}>
                      <FormControl>
                        <FormLabel>Has the customer signed their art confirmation?</FormLabel>
                        <Select name="signedArtwork" value={form.signedArtwork} onChange={handleChange} placeholder="Select">
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                          <option value="Not applicable">Not applicable</option>
                        </Select>
                      </FormControl>
                    </GridItem>
                  </Grid>
                  <Box mt={3}>
                    <StackModeField
                      value={form.cabinetStackMode || STACK_MODES.Stacked}
                      onChange={(v) => setForm(draft => { draft.cabinetStackMode = v })}
                    />
                    <ExtraStructureComponents form={form} setForm={setForm} type={STRUCTURE_COMPONENT_TYPES.Cabinet} />
                  </Box>
                </Box>
              )}
              {form.noMonument == false && (
                <Box>
                  <Heading size="md" mb={2}>Monument</Heading>
                  <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Style</FormLabel>
                        <Select name="monumentStyle" value={form.monumentStyle} onChange={handleChange} placeholder="Select">
                          {Object.keys(MONUMENT_STYLES).map(v => (
                            <option key={MONUMENT_STYLES[v]} value={MONUMENT_STYLES[v]}>{MONUMENT_STYLES[v]}</option>
                          ))}
                        </Select>
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>
              )}
              {form.noPedestal == false ? (
                <Box>
                  <Heading size="md" mb={2}>Pedestal / Pole Cover</Heading>
                  <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Style</FormLabel>
                        <Select name="pedestalStyle" value={form.pedestalStyle} onChange={handleChange} placeholder="Select">
                          {Object.keys(PEDESTAL_STYLES).map(v => (
                            <option key={PEDESTAL_STYLES[v]} value={PEDESTAL_STYLES[v]}>{PEDESTAL_STYLES[v]}</option>
                          ))}
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Nominal Size</FormLabel>
                        <Input name="pedestalNominalSize" value={form.pedestalNominalSize} onChange={handleChange} />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Decoration</FormLabel>
                        <UiSelect
                          value={form.pedestalDecorationOptionObj}
                          onChange={v => { setForm(draft => { draft.pedestalDecorationOptionObj = v }) }}
                          loadData={(page, search) => {
                            return getPedestalDecorationOptions(page, 20, search, 'description', 'asc')
                          }}
                          apiRespProp="rows"
                          getOptionValue={v => v.id}
                          getOptionLabel={v => v.description}
                          placeholder="Search"
                          noOptionsMessage={() => 'Not Found'}
                          required={true}
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>
              ) : (
                <Box>
                  <Heading size="md" mb={2}>Pedestal / Pole Cover</Heading>
                  <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Additional Height</FormLabel>
                        <Input name="pedestalAdditionalHeight" value={form.pedestalAdditionalHeight} onChange={handleChange} />
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>
              )}
              <Box>
                <Heading size="md" mb={2}>Support</Heading>
                <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>
                        {/wall mount/i.test(form.installationOptionObj?.description || '') ||
                        /wall mount brackets/i.test(form.verticalSupportOptionObj?.description || '')
                          ? 'Bracket Pairs'
                          : '# of Supports'}
                      </FormLabel>
                      <Input name="numberOfSupports" value={form.numberOfSupports} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Support Option</FormLabel>
                      <UiSelect
                        value={form.verticalSupportOptionObj}
                        onChange={v => { setForm(draft => { draft.verticalSupportOptionObj = v }) }}
                        loadData={(page, search) => {
                          return getVerticalSupportOptions(page, 20, search, 'description', 'asc')
                        }}
                        apiRespProp="rows"
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.description}
                        placeholder="Search"
                        noOptionsMessage={() => 'Not Found'}
                        required={true}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Footer Style Option</FormLabel>
                      <UiSelect
                        value={form.footerStyleOptionObj}
                        onChange={v => { setForm(draft => { draft.footerStyleOptionObj = v }) }}
                        loadData={(page, search) => {
                          return getFooterStyleOptions(page, 20, search, 'description', 'asc')
                        }}
                        apiRespProp="rows"
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.description}
                        placeholder="Search"
                        noOptionsMessage={() => 'Not Found'}
                        required={true}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
              <Box>
                <Heading size="md" mb={2}>Extra</Heading>
                <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Installation</FormLabel>
                      <UiSelect
                        value={form.installationOptionObj}
                        onChange={v => { setForm(draft => { draft.installationOptionObj = v }) }}
                        loadData={(page, search) => {
                          return getInstallationOptions(page, 20, search, 'description', 'asc')
                        }}
                        apiRespProp="rows"
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.description}
                        placeholder="Search"
                        noOptionsMessage={() => 'Not Found'}
                        required={true}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Site Survey</FormLabel>
                      <UiSelect
                        value={form.siteSurveyOptionObj}
                        onChange={v => { setForm(draft => { draft.siteSurveyOptionObj = v }) }}
                        loadData={(page, search) => {
                          return getSiteSurveyOptions(page, 20, search, 'description', 'asc')
                        }}
                        apiRespProp="rows"
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.description}
                        placeholder="Search"
                        noOptionsMessage={() => 'Not Found'}
                        required={true}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Existing Sign</FormLabel>
                      <UiSelect
                        value={form.existingSignOptionObj}
                        onChange={v => { setForm(draft => { draft.existingSignOptionObj = v }) }}
                        loadData={(page, search) => {
                          return getExistingSignOptions(page, 20, search, 'description', 'asc')
                        }}
                        apiRespProp="rows"
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.description}
                        placeholder="Search"
                        noOptionsMessage={() => 'Not Found'}
                        required={true}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
              <Box>
                <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Signed Quote</FormLabel>
                      <Input type="file" name="fileSignedQuote" onChange={handleFileChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Signed Art</FormLabel>
                      <Input type="file" name="fileSignedArt" onChange={handleFileChange} />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
              <Box>
                <FormControl>
                  <FormLabel>Scope</FormLabel>
                  <Textarea name="scope" value={form.scope} onChange={handleChange} />
                </FormControl>
              </Box>
              <Box>
                <Heading size="md" mb={2}>Accounting Information</Heading>
                <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>PO (if available)</FormLabel>
                      <Input name="customerPo" value={form.customerPo} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Tax Exemption (if available)</FormLabel>
                      <Input type="file" name="fileTaxExemption" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Expected Payment Terms</FormLabel>
                      <Select name="expectedPaymentTerms" value={form.expectedPaymentTerms} onChange={handleChange} placeholder="Select">
                        {Object.keys(QUOTE_PAYMENT_TERMS_OPTIONS).map(v => (
                          <option key={QUOTE_PAYMENT_TERMS_OPTIONS[v]} value={QUOTE_PAYMENT_TERMS_OPTIONS[v]}>{QUOTE_PAYMENT_TERMS_OPTIONS[v]}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Is customer providing a deposit?</FormLabel>
                      <Select name="customerProvidingDeposit" value={form.customerProvidingDeposit} onChange={handleChange}>
                        {Object.keys(CUSTOMER_PROVIDING_DEPOSIT).map(v => (
                          <option key={CUSTOMER_PROVIDING_DEPOSIT[v]} value={CUSTOMER_PROVIDING_DEPOSIT[v]}>{CUSTOMER_PROVIDING_DEPOSIT[v]}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <FormControl>
                      <FormLabel>Alternate Accounting Contact (if provided)</FormLabel>
                      <Input name="alternateAccountingContact" value={form.alternateAccountingContact} onChange={handleChange} placeholder="Name, email, phone" />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <FormControl>
                      <Checkbox
                        isChecked={form.isIndividualSchool}
                        onChange={(e) => setForm(draft => {
                          draft.isIndividualSchool = e.target.checked
                          if (!e.target.checked) draft.schoolDistrictName = ''
                        })}
                      >
                        Project is an individual school
                      </Checkbox>
                    </FormControl>
                  </GridItem>
                  {form.isIndividualSchool && (
                    <GridItem colSpan={2}>
                      <FormControl>
                        <FormLabel>School District Name</FormLabel>
                        <Input name="schoolDistrictName" value={form.schoolDistrictName} onChange={handleChange} />
                      </FormControl>
                    </GridItem>
                  )}
                  <GridItem colSpan={2}>
                    <FormControl>
                      <FormLabel>Notes</FormLabel>
                      <Textarea name="accountingNotes" value={form.accountingNotes} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end" gap={4}>
              <Button type="submit" colorScheme="blue" isLoading={isSaving}>
                Save
              </Button>
              <Button onClick={onClose}>Close</Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

ModalProjectDetailOverview.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  projectId: PropTypes.number,
  projectExtraDetail: PropTypes.object,
  reload: PropTypes.func
}
